'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import { MONO_FONT, SERIF_FONT } from '@/theme';

/**
 * Tarjeta de abogada/o. Foto (o placeholder con textura), nombre, rol y una
 * bio breve.
 *
 * - `href` → la tarjeta enlaza al perfil completo y muestra "Ver perfil".
 * - sin `href` → variante autónoma con `bio[]` (párrafos) y fila de contacto.
 */
export default function PersonCard({ person, href }) {
  const {
    name,
    role,
    photo,
    photoPosition,
    bioShort,
    bio = [],
    languages = [],
    email,
    tel,
    telDisplay,
    linkedin,
  } = person;

  const inner = (
    <>
      <Box
        sx={{
          aspectRatio: '1 / 1',
          display: 'flex',
          alignItems: 'flex-end',
          p: '20px',
          ...(photo
            ? {
                backgroundImage: `url(${photo})`,
                backgroundSize: 'cover',
                backgroundPosition: photoPosition || 'top',
              }
            : {
                background:
                  'repeating-linear-gradient(135deg,#eae3d6,#eae3d6 14px,#f2ece0 14px,#f2ece0 28px)',
              }),
        }}
      >
        {!photo && (
          <Box
            component="span"
            sx={{
              fontFamily: MONO_FONT,
              fontSize: '11px',
              letterSpacing: '0.12em',
              color: '#9a8f78',
              textTransform: 'uppercase',
            }}
          >
            retrato · {name}
          </Box>
        )}
      </Box>

      <Box sx={{ p: { xs: '28px', md: '32px 32px 34px' } }}>
        <Typography
          component="h3"
          sx={{
            fontFamily: SERIF_FONT,
            fontSize: '26px',
            fontWeight: 600,
            color: 'text.primary',
          }}
        >
          {name}
        </Typography>
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            fontFamily: MONO_FONT,
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.24em',
            color: 'primary.dark',
            textTransform: 'uppercase',
            my: '14px',
          }}
        >
          {role}
        </Box>

        {href ? (
          <>
            {bioShort && (
              <Typography
                sx={{
                  fontSize: '14.5px',
                  lineHeight: 1.75,
                  color: 'text.secondary',
                }}
              >
                {bioShort}
              </Typography>
            )}
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                mt: '20px',
                fontFamily: MONO_FONT,
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: 'primary.dark',
              }}
            >
              Ver perfil
              <Box component="span" aria-hidden="true">
                →
              </Box>
            </Box>
          </>
        ) : (
          <>
            {bio.map((p, i) => (
              <Typography
                key={i}
                sx={{
                  fontSize: '14.5px',
                  lineHeight: 1.75,
                  color: 'text.secondary',
                  mb: 2,
                }}
              >
                {p}
              </Typography>
            ))}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px 20px',
                borderTop: '1px solid',
                borderColor: 'cream.lineSoft',
                pt: '18px',
                mt: '4px',
                fontSize: '13px',
                color: 'text.disabled',
              }}
            >
              {languages.length > 0 && <span>{languages.join(' · ')}</span>}
              {email && (
                <Box
                  component="a"
                  href={`mailto:${email}`}
                  sx={{ color: 'primary.dark' }}
                >
                  {email}
                </Box>
              )}
              {tel && (
                <Box
                  component="a"
                  href={`tel:${tel}`}
                  sx={{ color: 'primary.dark' }}
                >
                  {telDisplay || tel}
                </Box>
              )}
              {linkedin && (
                <Box
                  component="a"
                  href={linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'primary.dark' }}
                >
                  LinkedIn
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </>
  );

  const sx = {
    display: 'block',
    height: '100%',
    bgcolor: '#fff',
    border: '1px solid',
    borderColor: 'cream.line',
    color: 'inherit',
    transition: 'border-color 0.2s ease',
    ...(href && { '&:hover': { borderColor: 'primary.main' } }),
  };

  return href ? (
    <Box component={Link} href={href} sx={sx}>
      {inner}
    </Box>
  ) : (
    <Box component="article" sx={sx}>
      {inner}
    </Box>
  );
}
